// Fill the Orion direct-job activity log (LALI / EFFY / Iberjoya / MWI) and
// return a downloadable Blob. Used for hourly+mileage direct jobs that aren't
// Malca-Amit. DAI is flat rate and gets no workbook.
//
// Cell map (verified against the 2026 contractor template):
//   D6        → Last Name, First name or Company   (template pre-fills "ST Services")
//   D7        → Contact number                     (template pre-fills)
//   H7        → Email                              (template pre-fills)
//   D9        → Date of Job (MM/DD/YY)
//   D11       → Sales Rep's Name
//   K11       → Sales Rep's Company (= broker name)
//   E13       → Pick-Up Location (Name/Address)
//   E14..E16  → "Next Location" — used only on multi-stop direct (rare)
//   E17       → Drop Off Location (Name/Address)
//   D21       → Job started/arrived at pickup
//   D22       → Time departed pick-up
//   D25       → Arrival time at drop off
//   D26       → Time departed drop off
//   K25       → Miles from pickup to drop off
//   K27       → Miles back to pickup (= same as K25 — form sums for round-trip)
//   B29..B31  → Expense amounts (left side); D29..D31 are detail labels, E29..E31 details
//   A33       → Additional Comments
//
// Auto-calculated by template formulas: D23 (travel time), D27 (back-at-pickup),
// C38 (raw hours), D38 (charged hours, 3hr min), E38 (hours $), C39 (whole miles),
// E39 (mileage $), E40 (expenses sum), K40 (total due).

import ExcelJS from "exceljs";

const TEMPLATE_URL = "/templates/orion-direct-2026.xlsx";

// ── Helpers (mirror malcaExcel.js so the two stay close) ─────────────

function tsToDate(ts) {
  if (!ts) return null;
  let ms = null;
  if (typeof ts === "number") ms = ts;
  else if (typeof ts === "string") ms = Date.parse(ts);
  else if (ts.toMillis) ms = ts.toMillis();
  else if (ts.seconds != null) ms = ts.seconds * 1000;
  if (ms == null || Number.isNaN(ms)) return null;
  return new Date(ms);
}

function tsToHHMM(ts) {
  const d = tsToDate(ts);
  if (!d) return null;
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function hhmmToSerial(hhmm) {
  if (!hhmm) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm).trim());
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  if (h > 23 || mi > 59) return null;
  return h / 24 + mi / 1440;
}

function setVal(ws, addr, value) {
  if (value === null || value === undefined || value === "") return;
  ws.getCell(addr).value = value;
}

function setTime(ws, addr, hhmm) {
  const serial = hhmmToSerial(hhmm);
  if (serial == null) return;
  const cell = ws.getCell(addr);
  cell.value = serial;
  // Force 24-hour military format. Cloning the cell.style first ensures the
  // override doesn't get lost when multiple cells share a style object in the
  // template (which they do).
  const newStyle = cell.style ? { ...cell.style } : {};
  newStyle.numFmt = "hh:mm";
  cell.style = newStyle;
  cell.numFmt = "hh:mm";
}

function ceilMiles(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.ceil(n);
}

function locLine(stop) {
  if (!stop) return "";
  const name = stop.locationName || "";
  const addr = stop.address || "";
  if (name && addr) return `${name} — ${addr}`;
  return name || addr;
}

// ── Main ─────────────────────────────────────────────────────────────

/**
 * Build the filled Orion direct workbook as a Blob.
 *
 * For multi-guard jobs, drivers (#1, #3) get the mileage filled in; non-drivers
 * get zero mileage but the same hours and locations.
 *
 * @param {object} job
 * @param {object} [broker]
 * @param {object} [opts]
 * @param {number} [opts.guardNumber] — 1..4. If omitted, treat as single-guard.
 * @param {string} [opts.guardName]
 * @param {boolean}[opts.isDriver]   — when false, blank out K25/K27 mileage.
 * @returns {Promise<Blob>}
 */
export async function generateOrionDirectWorkbook(job, broker /* eslint-disable-line no-unused-vars */, opts = {}) {
  const res = await fetch(TEMPLATE_URL);
  if (!res.ok) throw new Error(`Couldn't load Orion direct template (${res.status}). Make sure public/templates/orion-direct-2026.xlsx exists.`);
  const ab = await res.arrayBuffer();

  const wb = new ExcelJS.Workbook();
  await wb.xlsx.load(ab);
  const ws = wb.worksheets[0];

  wb.calcProperties = wb.calcProperties || {};
  wb.calcProperties.fullCalcOnLoad = true;

  // The template's D23 = TEXT(D25-D22,"H:MM") is a STRING-valued formula. On
  // save, ExcelJS drops its t="str" type and serializes the cached result as
  // <v>NaN</v>, which Excel rejects as corrupt ("Repaired Records: Cell
  // information"). Drop the cached result on every non-numeric formula cell so
  // Excel just recomputes it on open (fullCalcOnLoad is set above). Numeric
  // formula caches serialize fine and are left intact.
  ws.eachRow((row) => {
    row.eachCell({ includeEmpty: false }, (cell) => {
      if (cell.type === ExcelJS.ValueType.Formula) {
        const r = cell.result;
        if (typeof r !== "number" || !Number.isFinite(r)) {
          cell.value = { formula: cell.formula };
        }
      }
    });
  });

  const stops = Array.isArray(job.stops) ? job.stops : [];
  // Direct jobs have no airport. Filter defensively in case the data model
  // includes one anyway.
  const realStops = stops.filter((s) => s.type !== "airport");
  const pickup = realStops[0] || null;
  const dropoff = realStops[realStops.length - 1] || null;
  const middles = realStops.slice(1, -1); // intermediates (rare)

  // ── Header ─────────────────────────────────────────────────────────
  // D6/D7/H7 are pre-filled in the template with the contractor's info — leave
  // them alone.
  // D9 = date of job. Prefer the scheduled (canonical) job date; parse the
  // YYYY-MM-DD string at local noon so the calendar date can't shift across a
  // timezone boundary. Fall back to the completed/started timestamps.
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(job.scheduledDate || "");
  const detailDate = ymd
    ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]), 12, 0, 0)
    : tsToDate(job.completedAt || job.completedAtMs || job.startedAt);
  if (detailDate) {
    const c = ws.getCell("D9");
    c.value = detailDate;
    if (!c.numFmt) c.numFmt = "mm/dd/yy";
  }
  // D11 = account rep (sales rep) name. K11 = broker — this form labels that
  // column "Sales Rep's Company", which for our jobs is the broker.
  setVal(ws, "D11", job.salesRepName || "");
  setVal(ws, "K11", broker?.name || job.brokerName || "");

  // ── Locations ──────────────────────────────────────────────────────
  setVal(ws, "E13", locLine(pickup));
  middles.slice(0, 3).forEach((stop, idx) => {
    setVal(ws, `E${14 + idx}`, locLine(stop));
  });
  setVal(ws, "E17", locLine(dropoff));

  // ── Times ──────────────────────────────────────────────────────────
  // We track one time per stop in the app today. Map the four template slots
  // as best we can; the owner can edit any of them in Excel before sending.
  const stopTimes = Array.isArray(job.stopBillingTimes) ? job.stopBillingTimes : [];
  function stopTime(stop) {
    if (!stop) return null;
    const fullIdx = stops.indexOf(stop);
    return stopTimes[fullIdx] || tsToHHMM(stop.completedAt) || stop.scheduledTime || null;
  }

  // D21 — arrived at pickup. Use job.pickupStartTime which already auto-derives
  // 30 min before earliest scheduled (or owner override).
  setTime(ws, "D21", job.pickupStartTime);
  // D22 — time departed pickup. Use the pickup stop's billing time (when guard
  // finished collecting). If we have nothing distinct, mirror D21 so the
  // travel-time formula doesn't go negative.
  const departPickup = stopTime(pickup) || job.pickupStartTime;
  setTime(ws, "D22", departPickup);
  // D25 — arrival at drop off.
  const arriveDropoff = stopTime(dropoff);
  setTime(ws, "D25", arriveDropoff);
  // D26 — time departed drop off. Prefer the explicit departedDropoffTime
  // from the Orion direct timeline; fall back to legacy returnTime (older
  // jobs), then the drop-off arrival. The template's D27 formula derives
  // "arrival back to pickup" from D26 + the travel time, so we must NOT put
  // the arrival-back time here. Hoisted to a variable so the cached-totals
  // stamp below can re-derive the same timeline.
  const departDropoff = job.departedDropoffTime || job.returnTime || arriveDropoff;
  setTime(ws, "D26", departDropoff);

  // ── Mileage ────────────────────────────────────────────────────────
  // segments[0]   = dispatch → pickup (starting; we don't bill this on direct)
  // segments[1]   = pickup → dropoff (single-stop case)
  // segments[i]   = stop[i-1] → stop[i] for multi-stop
  // segments[N]   = return leg (Google-mapped)
  const segs = Array.isArray(job.mileageSegments) ? job.mileageSegments : [];

  // Sum the one-way distance from pickup to dropoff including any intermediates.
  // For a 2-stop direct that's just segments[1]. For a 3-stop direct it's
  // segments[1] + segments[2] = pickup→middle→dropoff.
  let oneWay = 0;
  for (let j = 1; j < realStops.length; j++) {
    oneWay += Number(segs[j]) || 0;
  }
  const oneWayCeil = ceilMiles(oneWay);
  // Per-guard rule: only drivers (#1, #3) submit mileage. Non-drivers leave
  // K25/K27 blank.
  if (opts.isDriver !== false) {
    // K25 = miles from pickup to drop off; K27 = same number again (form sums
    // them for the round-trip total per Orion FAQ doubling rule).
    setVal(ws, "K25", oneWayCeil);
    setVal(ws, "K27", oneWayCeil);
  }
  // K26 — misc. mileage / extra stops; leave blank unless we ever surface a
  // "misc miles" field. (Multi-stop direct is rare; the user said "not typical".)

  // ── Expenses ───────────────────────────────────────────────────────
  // Single consolidated expense → first slot (B29 amount, E29 details).
  const exp = Number(job.expenses);
  if (Number.isFinite(exp) && exp > 0) {
    setVal(ws, "B29", exp);
    setVal(ws, "E29", job.expensesDetail || "Expenses");
  }

  // ── Additional Comments (A33) ──────────────────────────────────────
  // A33:L35 is ONE merged box, so only the anchor (A33) displays — the old
  // code wrote the notes to A34 (a non-anchor cell), so they never showed,
  // and put "Activity log for: Guard N" in A33. Drop that header (the guard
  // is already identified by the per-guard filename) and write the actual
  // notes to A33. Non-driver guards keep a short annotation so the broker
  // knows why their mileage is blank.
  const bodyParts = [];
  if (opts.isDriver === false) bodyParts.push("(non-driver — no mileage)");
  if (job.billingNotes) bodyParts.push(job.billingNotes);
  const stopsArr = Array.isArray(job.stops) ? job.stops : [];
  const stopNotes = stopsArr
    .map((s) => {
      const n = (s.note || "").trim();
      if (!n) return null;
      const where = s.locationName || s.address || "stop";
      return `${where}: ${n}`;
    })
    .filter(Boolean);
  if (stopNotes.length) bodyParts.push(stopNotes.join(" · "));
  if (bodyParts.length) setVal(ws, "A33", bodyParts.join(" — "));

  // ── Cached formula results ──────────────────────────────────────────
  // The template's totals (hours / mileage / $) are formulas that Excel
  // recomputes on open via fullCalcOnLoad — but ExcelJS silently never
  // persists that flag, so anything that skips the recalculation —
  // Protected View before "Enable Editing", attachment previews, phone
  // viewers — displays the STORED result instead (blank or the template's
  // stale $120). Stamp the computed results into the formula cells so the
  // totals read correctly everywhere; a real recalc produces the same
  // numbers. Mirrors the template's own formulas exactly (F38/F39/F40/L40
  // are merge slaves of the E/K anchors, so stamping anchors covers all):
  //   D23 = TEXT(D25-D22,"H:MM")       travel time (string; recalc-only)
  //   D27 = D26+D23                    arrival back at pickup
  //   C38 = MOD(D27-D21,1)*24          raw hours
  //   D38 = ROUND(IF(C38<3,3,C38),2)   3h Orion minimum
  //   E38 = D38*40                     hourly $
  //   C39 = ROUNDUP(SUM(K25:L27),0)    total miles (K25 + K27 round trip)
  //   E39 = C39*0.725                  mileage $
  //   E40 = SUM(B29:B31,H29:H31)       expenses
  //   K40 = SUM(E38:F40)               grand total
  const setCachedResult = (addr, result) => {
    if (result == null || !Number.isFinite(result)) return;
    const cell = ws.getCell(addr);
    const v = cell.value;
    if (v && typeof v === "object" && v.formula) {
      cell.value = { formula: v.formula, result: Math.round(result * 100) / 100 };
    }
  };
  const minutesOf = (hhmm) => {
    const serial = hhmmToSerial(hhmm);
    return serial == null ? null : Math.round(serial * 1440);
  };
  const milesTotal = opts.isDriver !== false ? oneWayCeil * 2 : 0;
  const expTotal = Number.isFinite(exp) && exp > 0 ? exp : 0;
  setCachedResult("C39", milesTotal);
  setCachedResult("E39", milesTotal * 0.725);
  setCachedResult("E40", expTotal);
  const startMin = minutesOf(job.pickupStartTime); // D21
  const departMin = minutesOf(departPickup); // D22
  const arriveMin = minutesOf(arriveDropoff); // D25
  const departDropMin = minutesOf(departDropoff); // D26
  if (startMin != null && departMin != null && arriveMin != null && departDropMin != null) {
    const travelMin = (arriveMin - departMin + 1440) % 1440; // D23
    const backMin = departDropMin + travelMin; // D27
    const rawHours = ((((backMin - startMin) % 1440) + 1440) % 1440) / 60; // C38
    const hoursCharged = Math.round(Math.max(3, rawHours) * 100) / 100; // D38
    setCachedResult("C38", rawHours);
    setCachedResult("D38", hoursCharged);
    setCachedResult("E38", hoursCharged * 40);
    setCachedResult("K40", hoursCharged * 40 + milesTotal * 0.725 + expTotal);
  }

  const out = await wb.xlsx.writeBuffer();
  return new Blob([out], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

/** Trigger a download. */
export async function downloadOrionDirectWorkbook(job, broker) {
  const blob = await generateOrionDirectWorkbook(job, broker);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${job.jobNumber || job.id || "job"}-Orion-Direct.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
